import React, { useState, useEffect } from "react";
import { OFFERS } from "@/mock";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "@/components/ui/sonner";
import { ArrowRight } from "lucide-react";
import { DEFAULT_SITE_COPY, useSiteCopy, PageShell, PrimaryCTA } from "./publicShared";

const API = process.env.REACT_APP_BACKEND_URL;

export const PricingContent = () => {
  const { copy } = useSiteCopy();
  const [offers, setOffers] = useState(OFFERS);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await fetch(`${API}/api/pricing/public`);
        if (!res.ok) return;
        const data = await res.json();
        const p = data.pricing || {};
        const plans = data.retainer_plans || [];
        const lowestRetainer = plans.length > 0
          ? plans.reduce((min, r) => r.monthly_price < min.monthly_price ? r : min, plans[0])
          : null;

        const dynamicOffers = [
          {
            key: "score",
            title: "Preliminary Assessment",
            price: "FREE",
            subtitle: "Smart intake to identify your top workflow opportunities",
            output: "Opportunity Snapshot + Top 3 workflows + Readiness indicators + Recommended next step",
            stage: "Start",
          },
          p.assessment ? {
            key: "assessment",
            title: p.assessment.label,
            price: `$${Number(p.assessment.amount).toLocaleString()}`,
            subtitle: p.assessment.description,
            output: "Workflow analysis + Prioritized opportunities + Readiness & security lens + Action plan + Portal setup",
            tooltip: "Pricing based on business size, workflow complexity, systems landscape, data readiness, and regulatory needs.",
            stage: "Assess",
          } : OFFERS[1],
          p.implementation ? {
            key: "implementation",
            title: p.implementation.label,
            price: `$${Number(p.implementation.amount).toLocaleString()}`,
            subtitle: p.implementation.description,
            output: `${Math.round(p.included_workflows?.amount || 3)} workflow automations + Milestones + Timeline + KPI dashboard + Value tracking`,
            stage: "Build",
          } : OFFERS[2],
          lowestRetainer ? {
            key: "retainer",
            title: "CAIO Retainer",
            price: `Starting at $${Number(lowestRetainer.monthly_price).toLocaleString()}/mo`,
            subtitle: lowestRetainer.summary_description,
            output: "Monitoring + Optimization + Quarterly roadmap refresh + 1 micro-enhancement/month",
            stage: "Scale",
          } : OFFERS[3],
        ];
        setOffers(dynamicOffers);
      } catch {
        // fall back to static OFFERS
      }
    };
    fetchPricing();
  }, []);

  return (
    <PageShell>
      <h1 className="text-3xl font-semibold tracking-tight text-foreground dark:text-white">
        Pricing
      </h1>
      <p className="mt-3 max-w-2xl text-foreground/65 dark:text-white/65">
        {copy.pricing_intro || DEFAULT_SITE_COPY.pricing_intro}
      </p>

      <div className="mt-10 grid gap-4 md:grid-cols-2">
        {offers.map((o) => (
          <Card
            key={o.key}
            className="rounded-3xl border-border/60 dark:border-white/10 bg-background/60 dark:bg-white/5 p-6 backdrop-blur-2xl"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-sm text-foreground/80 dark:text-white/80">{o.title}</div>
                <div className="mt-1 text-2xl font-semibold text-foreground dark:text-white">
                  {o.price}
                </div>
                <div className="mt-2 text-sm text-foreground/65 dark:text-white/65">{o.subtitle}</div>
              </div>
              <div className="rounded-full border border-border/60 dark:border-white/10 bg-muted/60 dark:bg-black/40 px-3 py-1 text-xs text-foreground/70 dark:text-white/70">
                {o.stage || "Gate"}
              </div>
            </div>

            <div className="mt-5 text-sm leading-relaxed text-foreground/65 dark:text-white/65">
              {o.output}
            </div>

            <div className="mt-6">
              {o.tooltip ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="secondary"
                        className="h-11 rounded-none border border-border/60 dark:border-white/10 bg-background/60 dark:bg-white/5 text-foreground dark:text-white hover:bg-foreground hover:text-background"
                      >
                        What drives cost?
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs border-border/60 dark:border-white/10 bg-popover text-popover-foreground">
                      {o.key === "detailed" && copy.detailed_tooltip
                        ? copy.detailed_tooltip
                        : o.tooltip}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <Button
                  onClick={() =>
                    toast.message("Next step", {
                      description: "Start with the free assessment to get a tailored recommendation.",
                    })
                  }
                  className="h-11 rounded-none bg-[var(--brand)] text-white hover:bg-[var(--brand)]"
                >
                  Start with free assessment
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>

      <div className="mt-12">
        <PrimaryCTA copy={copy} />
      </div>
    </PageShell>
  );
};
